import { Component, OnInit, ViewChild } from '@angular/core';
import { MatTableDataSource, MatPaginator, MatSnackBar } from '@angular/material';
import { SelectionModel } from '@angular/cdk/collections';
import { AdminService } from '../admin.service';
import * as XLSX from 'xlsx';

interface ImportRow {
  _rowIndex: number;
  _duplicateStatus: 'strong' | 'weak' | 'new';
  _duplicateInfo?: string;
  student_id?: string;
  firstname?: string;
  middlenames?: string;
  lastname?: string;
  birthdate?: any;
  emailaddress?: string;
  lastyear?: number;
  firstyear?: number;
  housename?: string;
  border?: boolean;
  fromyear?: string;
  toyear?: string;
  organisationalnotes?: string;
  gender?: string;
  title?: string;
  wbhsOldBoy?: boolean;
  address1?: string;
  suburb?: string;
  town_city?: string;
  postalcode?: string;
  mobiletelephone?: string;
  firstclass?: string;
  lastclass?: string;
  [key: string]: any;
}

// Normalize header text: lowercase, strip apostrophes/special chars, collapse whitespace
function normalizeHeader(h: string): string {
  return h.replace(/\n/g, ' ').replace(/[''`]/g, '').replace(/[^a-z0-9 ]/gi, ' ')
    .toLowerCase().replace(/\s+/g, ' ').trim();
}

// Map known header variations to Prospect field names
const headerMap: { [key: string]: string } = {
  'student id': 'student_id',
  'studentid': 'student_id',
  'date of birth': 'birthdate',
  'dob': 'birthdate',
  'first name': 'firstname',
  'firstname': 'firstname',
  'middle name': 'middlenames',
  'middlenames': 'middlenames',
  'middle': 'middlenames',
  'last name': 'lastname',
  'lastname': 'lastname',
  'surname': 'lastname',
  'address': 'address1',
  'suburb': 'suburb',
  'town': 'town_city',
  'city': 'town_city',
  'postcode': 'postalcode',
  'postal code': 'postalcode',
  'enrollment date': '_enrollment_date',
  'enrolment date': '_enrollment_date',
  'date enrolled': '_enrollment_date',
  'leaving date': '_leaving_date',
  'date left': '_leaving_date',
  'boarder': 'border',
  'carruth boarder': 'border',
  'destination': '_destination',
  'leaving notes': '_leaving_notes',
  'notes': '_notes',
  'student cell': 'mobiletelephone',
  'mobile': 'mobiletelephone',
  'cell': 'mobiletelephone',
  'email 1': 'emailaddress',
  'email': 'emailaddress',
  'email 2': 'emailaddress2',
  'student email': '_student_email',
  'whanau': 'housename',
  'house': 'housename',
  'class': 'lastclass',
  'level': '_level',
  'rd': '_rd',
  'enac': '_enac',
  'parent phone': '_parent_phone',
  'parent phone 2': '_parent_phone2',
  'mothers name': '_mothers_name',
  'mother name': '_mothers_name',
  'mum': '_mothers_name',
  'fathers name': '_fathers_name',
  'father name': '_fathers_name',
  'dad': '_fathers_name',
};

@Component({
  selector: 'app-import',
  templateUrl: './import.component.html',
  styleUrls: ['./import.component.css']
})
export class ImportComponent implements OnInit {

  @ViewChild(MatPaginator) paginator: MatPaginator;

  // Step tracking
  step = 1;

  // File/sheet state
  workbook: XLSX.WorkBook;
  sheetNames: string[] = [];
  selectedSheet = '';
  fileName = '';

  // Table state
  dataSource = new MatTableDataSource<ImportRow>();
  selection = new SelectionModel<ImportRow>(true, []);
  displayedColumns: string[] = [
    'select', 'duplicateStatus', 'student_id', 'firstname', 'middlenames',
    'lastname', 'birthdate', 'emailaddress', 'lastyear', 'housename'
  ];

  // Import state
  importing = false;
  checkingDuplicates = false;
  importResult: string = null;

  // Stats
  totalRows = 0;
  strongDuplicates = 0;
  weakDuplicates = 0;
  newRecords = 0;

  constructor(
    private adminService: AdminService,
    private snackBar: MatSnackBar
  ) {}

  ngOnInit() {}

  onFileSelected(event: any) {
    const file: File = event.target.files[0];
    if (!file) return;

    this.fileName = file.name;
    const reader = new FileReader();
    reader.onload = (e: any) => {
      const data = new Uint8Array(e.target.result);
      this.workbook = XLSX.read(data, { type: 'array' });
      this.sheetNames = this.workbook.SheetNames;
      this.step = 2;
    };
    reader.readAsArrayBuffer(file);
  }

  onSheetSelected() {
    if (!this.selectedSheet) return;

    const sheet = this.workbook.Sheets[this.selectedSheet];
    const rawData: any[][] = XLSX.utils.sheet_to_json(sheet, { header: 1, raw: true });

    if (rawData.length < 2) {
      this.snackBar.open('Sheet has no data rows.', 'Dismiss', { duration: 5000 });
      return;
    }

    // Parse headers
    const rawHeaders: string[] = rawData[0].map(h => String(h || '').trim());
    const fieldMapping: (string | null)[] = rawHeaders.map(h => {
      const normalized = normalizeHeader(h);
      return headerMap[normalized] || null;
    });

    // Parse data rows
    const rows: ImportRow[] = [];
    for (let i = 1; i < rawData.length; i++) {
      const raw = rawData[i];
      if (!raw || raw.length === 0) continue;

      const row: ImportRow = {
        _rowIndex: i,
        _duplicateStatus: 'new',
        gender: 'Male',
        title: 'Mr',
        wbhsOldBoy: true
      };

      let destination = '';
      let leavingNotes = '';
      let notes = '';
      let mothersName = '';
      let fathersName = '';

      for (let j = 0; j < fieldMapping.length; j++) {
        const field = fieldMapping[j];
        if (!field || raw[j] == null || raw[j] === '') continue;
        const val = raw[j];

        if (field === '_destination') {
          destination = String(val).trim();
        } else if (field === '_leaving_notes') {
          leavingNotes = String(val).trim();
        } else if (field === '_notes') {
          notes = String(val).trim();
        } else if (field === '_enrollment_date') {
          row.firstyear = this.extractYear(val);
        } else if (field === '_leaving_date') {
          row.lastyear = this.extractYear(val);
        } else if (field === 'border') {
          row.border = this.parseBoolean(val);
        } else if (field === 'birthdate') {
          row.birthdate = this.parseDate(val);
        } else if (field === '_student_email') {
          if (!row.emailaddress) {
            row.emailaddress = String(val).trim();
          }
        } else if (field === '_mothers_name') {
          mothersName = String(val).trim();
        } else if (field === '_fathers_name') {
          fathersName = String(val).trim();
        } else if (field === '_level' || field === '_rd' || field === '_enac' ||
                   field === '_parent_phone' || field === '_parent_phone2') {
          // skip these fields
        } else if (field === 'student_id') {
          row.student_id = String(val).trim();
        } else if (field === 'firstyear' || field === 'lastyear') {
          row[field] = typeof val === 'number' ? val : parseInt(val, 10) || null;
        } else {
          row[field] = typeof val === 'string' ? val.trim() : val;
        }
      }

      // Map father's name to parent_partner fields
      if (fathersName) {
        const parts = fathersName.split(/\s+/);
        row['parent_partnerfirstname'] = parts[0];
        if (parts.length > 1) {
          row['parent_partnerlastname'] = parts.slice(1).join(' ');
        }
      }

      // Concatenate notes (mother's name included since no dedicated field)
      const noteParts = [
        destination,
        leavingNotes,
        notes,
        mothersName ? 'Mother: ' + mothersName : ''
      ].filter(n => n);
      if (noteParts.length > 0) {
        row.organisationalnotes = noteParts.join(' | ');
      }

      // Skip rows with no name
      if (!row.firstname && !row.lastname) continue;

      rows.push(row);
    }

    this.totalRows = rows.length;
    this.dataSource.data = rows;
    this.dataSource.paginator = this.paginator;

    // Select all by default
    this.selection.clear();
    rows.forEach(r => this.selection.select(r));

    this.step = 3;
    this.importResult = null;

    // Check duplicates
    this.checkDuplicates(rows);
  }

  private checkDuplicates(rows: ImportRow[]) {
    this.checkingDuplicates = true;
    const studentIds = rows.map(r => r.student_id).filter(id => id != null && id !== '');
    const nameYearCombos = rows
      .filter(r => r.firstname && r.lastname && r.lastyear)
      .map(r => ({
        firstname: r.firstname,
        lastname: r.lastname,
        lastyear: r.lastyear
      }));

    this.adminService.checkDuplicates(studentIds, nameYearCombos).subscribe(
      (result: any) => {
        this.checkingDuplicates = false;
        if (!result || result.status) return; // error response

        this.strongDuplicates = 0;
        this.weakDuplicates = 0;
        this.newRecords = 0;

        rows.forEach(row => {
          // Check strong match (student_id)
          if (row.student_id && result.studentIdMatches && result.studentIdMatches[row.student_id]) {
            row._duplicateStatus = 'strong';
            const match = result.studentIdMatches[row.student_id];
            row._duplicateInfo = 'Student ID match: ' + match.firstname + ' ' + match.lastname + ' (' + match.lastyear + ')';
            this.selection.deselect(row);
            this.strongDuplicates++;
          }
          // Check weak match (name + year)
          else if (row.firstname && row.lastname && row.lastyear && result.nameYearMatches) {
            const key = (row.firstname + '|' + row.lastname + '|' + row.lastyear).toLowerCase();
            if (result.nameYearMatches[key]) {
              row._duplicateStatus = 'weak';
              const match = result.nameYearMatches[key];
              row._duplicateInfo = 'Name+Year match: ' + match.firstname + ' ' + match.lastname;
              this.weakDuplicates++;
            } else {
              row._duplicateStatus = 'new';
              this.newRecords++;
            }
          } else {
            row._duplicateStatus = 'new';
            this.newRecords++;
          }
        });

        // Refresh table
        this.dataSource.data = [...rows];
      },
      () => {
        this.checkingDuplicates = false;
        this.snackBar.open('Error checking duplicates.', 'Dismiss', { duration: 5000 });
      }
    );
  }

  doImport() {
    const selected = this.selection.selected;
    if (selected.length === 0) {
      this.snackBar.open('No rows selected for import.', 'Dismiss', { duration: 5000 });
      return;
    }

    this.importing = true;

    // Strip internal fields before sending
    const prospects = selected.map(row => {
      const clean: any = {};
      Object.keys(row).forEach(key => {
        if (!key.startsWith('_')) {
          clean[key] = row[key];
        }
      });
      return clean;
    });

    this.adminService.bulkImport(prospects).subscribe(
      (result: any) => {
        this.importing = false;
        if (result && result.success) {
          this.importResult = result.msg;
          this.snackBar.open(result.msg, 'Dismiss', { duration: 10000 });
          this.step = 4;
        } else {
          const msg = (result && result.msg) ? result.msg : 'Import failed.';
          this.snackBar.open(msg, 'Dismiss', { duration: 10000 });
        }
      },
      (err) => {
        this.importing = false;
        this.snackBar.open('Import failed: ' + (err.message || 'Unknown error'), 'Dismiss', { duration: 10000 });
      }
    );
  }

  // Helpers

  private extractYear(val: any): number | null {
    if (typeof val === 'number') {
      // Excel serial date
      if (val > 30000) {
        const parsed = XLSX.SSF.parse_date_code(val);
        return parsed ? parsed.y : null;
      }
      return val;
    }
    if (typeof val === 'string') {
      const d = new Date(val);
      if (!isNaN(d.getTime())) return d.getFullYear();
      const num = parseInt(val, 10);
      return isNaN(num) ? null : num;
    }
    return null;
  }

  private parseDate(val: any): string | null {
    if (typeof val === 'number' && val > 30000) {
      const parsed = XLSX.SSF.parse_date_code(val);
      if (parsed) {
        const m = String(parsed.m).padStart(2, '0');
        const d = String(parsed.d).padStart(2, '0');
        return parsed.y + '-' + m + '-' + d;
      }
    }
    if (typeof val === 'string') {
      const d = new Date(val);
      if (!isNaN(d.getTime())) return d.toISOString().slice(0, 10);
    }
    return null;
  }

  private parseBoolean(val: any): boolean {
    if (typeof val === 'boolean') return val;
    if (typeof val === 'number') return val !== 0;
    const s = String(val).trim().toLowerCase();
    return s === 'yes' || s === 'y' || s === 'true' || s === '1';
  }

  formatDate(val: any): string {
    if (!val) return '';
    if (typeof val === 'string') {
      const parts = val.split('-');
      if (parts.length === 3) return parts[2] + '/' + parts[1] + '/' + parts[0];
    }
    return String(val);
  }

  // Table selection helpers

  isAllSelected(): boolean {
    return this.selection.selected.length === this.dataSource.data.length;
  }

  masterToggle() {
    if (this.isAllSelected()) {
      this.selection.clear();
    } else {
      this.dataSource.data.forEach(row => this.selection.select(row));
    }
  }

  getDuplicateIcon(row: ImportRow): string {
    switch (row._duplicateStatus) {
      case 'strong': return 'error';
      case 'weak': return 'warning';
      default: return 'check_circle';
    }
  }

  getDuplicateColor(row: ImportRow): string {
    switch (row._duplicateStatus) {
      case 'strong': return '#f44336';
      case 'weak': return '#ff9800';
      default: return '#4caf50';
    }
  }

  getDuplicateTooltip(row: ImportRow): string {
    if (row._duplicateInfo) return row._duplicateInfo;
    return row._duplicateStatus === 'new' ? 'New record' : 'Possible duplicate';
  }

  resetImport() {
    this.step = 1;
    this.workbook = null;
    this.sheetNames = [];
    this.selectedSheet = '';
    this.fileName = '';
    this.dataSource.data = [];
    this.selection.clear();
    this.importResult = null;
    this.totalRows = 0;
    this.strongDuplicates = 0;
    this.weakDuplicates = 0;
    this.newRecords = 0;
  }
}
